import { DocumentConfigPartial, DocumentItemConfigsPartial } from '@domain';
import {
    CAPTION_POSITION_BELOW,
    CAPTION_POSITION_SIDE,
    CaptionStyle,
    normalizeCaptionStyle,
    resolveCaptionSide,
} from '../../../../../pdf-renderer/caption-style';
import { resolveMediaFill } from '../../../../../media-fill-options';

type ConfigKey = keyof DocumentItemConfigsPartial;

/**
 * Configs do card com uma chave trocada. Valor igual ao herdado do documento vira `null`:
 * o update-config do item MESCLA, então só `null` apaga a chave no servidor, e o card
 * volta a seguir o documento (mudar o documento depois o alcança).
 */
export function withOverride<K extends ConfigKey>(
    configs: DocumentItemConfigsPartial | undefined,
    key: K,
    value: DocumentItemConfigsPartial[K],
    inherited: DocumentItemConfigsPartial[K]
): DocumentItemConfigsPartial {
    return { ...(configs ?? {}), [key]: value === inherited ? null : value };
}

/** "Voltar ao padrão do documento": apaga só estas chaves (o resto, como o estilo do texto, fica). */
export function withoutKeys(
    configs: DocumentItemConfigsPartial | undefined,
    keys: readonly ConfigKey[]
): DocumentItemConfigsPartial {
    const next: Record<string, unknown> = { ...(configs ?? {}) };
    for (const key of keys) next[key] = null;
    return next as DocumentItemConfigsPartial;
}

/** Estilo da legenda trocado no card (igual ao do documento vira herança). */
export function withCaptionStyle(
    configs: DocumentItemConfigsPartial | undefined,
    style: CaptionStyle,
    documentConfig: DocumentConfigPartial | undefined
): DocumentItemConfigsPartial {
    return withOverride(configs, 'captionStyle', style, normalizeCaptionStyle(documentConfig?.captionStyle));
}

/** Descrição ao lado ligada ou desligada no card (igual ao documento vira herança). */
export function withCaptionSide(
    configs: DocumentItemConfigsPartial | undefined,
    side: boolean,
    documentConfig: DocumentConfigPartial | undefined
): DocumentItemConfigsPartial {
    const position = (value: boolean) => (value ? CAPTION_POSITION_SIDE : CAPTION_POSITION_BELOW);
    return withOverride(
        configs,
        'captionPosition',
        position(side),
        position(resolveCaptionSide([documentConfig]))
    );
}

/**
 * Cor do fundo da mídia trocada no card. Igual à efetiva do documento vira herança (`null`),
 * inclusive o cinza claro padrão quando o documento não define cor.
 */
export function withMediaFill(
    configs: DocumentItemConfigsPartial | undefined,
    color: string,
    documentConfig: DocumentConfigPartial | undefined
): DocumentItemConfigsPartial {
    return withOverride(configs, 'imageFillColor', color, resolveMediaFill([documentConfig]));
}

/** Alguma destas chaves está sobrescrita no card? */
export function hasAnyKey(
    configs: DocumentItemConfigsPartial | undefined,
    keys: readonly ConfigKey[]
): boolean {
    return keys.some(key => configs?.[key] != null);
}
